/**
 * Dashboard Layout - Claude.ai-inspired Design
 * Main layout with clean sidebar navigation and mobile menu
 */

import { useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { DarkModeToggle } from '../ui/ThemeProvider';
import GlobalSearchBar from '../Search/GlobalSearchBar';
import {
  LayoutDashboard,
  Calendar,
  Users,
  FileText,
  Menu,
  X,
  LogOut,
  TreePalm,
  Receipt,
} from 'lucide-react';

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Appointments', href: '/appointments', icon: Calendar },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Expenses', href: '/expenses', icon: Receipt },
];

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:text-sm focus:font-medium focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Mobile Menu Button - Claude-style minimal header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TreePalm className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold">Ishefa</h1>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="claude-button claude-button-ghost p-2"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Claude-style elegant sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-40 w-64 claude-sidebar transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo - Desktop Only */}
        <div className="hidden lg:flex items-center justify-between p-6 border-b border-border">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TreePalm className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-semibold tracking-tight">Ishefa</h1>
            </div>
            <p className="text-sm text-muted-foreground">Admin Dashboard</p>
          </div>
          <DarkModeToggle />
        </div>

        {/* Navigation - Claude-style nav items */}
        <nav className="p-4 space-y-1 mt-16 lg:mt-0" role="navigation" aria-label="Main navigation">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                className="claude-sidebar-item"
                data-tour={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User Section - Claude-style user card */}
        <div
          className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card/50"
          aria-label="User menu"
        >
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-semibold text-lg">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="claude-button claude-button-ghost w-full justify-start"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content - Claude-style spacious content area */}
      <div className="lg:pl-64 pt-16 lg:pt-0">
        {/* Desktop Search Header */}
        <div
          className="hidden lg:flex items-center justify-end px-10 pt-6 pb-0 max-w-7xl mx-auto"
          data-tour="search-bar"
        >
          <GlobalSearchBar />
        </div>
        <main
          id="main-content"
          tabIndex={-1}
          className="p-6 lg:px-10 lg:pb-10 lg:pt-4 max-w-7xl mx-auto"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
